import { FormEvent, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { ORGANIZATION_INVITATIONS_URL } from "@constants/routes";
import { USER_ROLE_TYPE_OPTIONS } from "@constants/user-role-type-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { AxnAdminUserPropertyBundleTypes } from "@enums/axn-admin-user-property-bundle-types";
import { LayoutTypes } from "@enums/layout-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { FormattedAxnAdminUserInvitation } from "@idw-types/data/axn-admin-user-invitation";
import { RelyingPartyRole, SelectOption } from "@idw-types/common";
import { IDWSession } from "@idw-types/data/idw-session";
import { updateOrganizationInvitationDetailsReduxRequest } from "@redux-requests/organization-invitation-details";
import { navigateToUtil } from "@utils/navigation-util";
import { extractRelyingPartiesForPropertyName, getRelyingPartyRolesWithPermissions, isAxnAdminUser } from "@utils/permissions-util";
import { getAllowedRelyingPartiesForOrganizationId } from "@utils/validation/organization/organization-users/organization-users-util";
import { LabelValueContainer } from "@containers/common/label-value-container/label-value-container";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { RelyingPartyRolesContainer } from "@containers/common/relying-party-roles-container/relying-party-roles-container";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import styles from "./organization-invitation-details-component.less";

type OrganizationInvitationDetailsComponentProps = {
    session: IDWSession;
    axnAdminUserInvitation: FormattedAxnAdminUserInvitation;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    isDisabled: boolean;
    setIsDisabled: (isDisabled: boolean) => void;
};

export const OrganizationInvitationDetailsComponent = (props: OrganizationInvitationDetailsComponentProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const {
        session,
        axnAdminUserInvitation,
        axnAdminUserPropertyBundles,
        axnCompanies,
        axnRelyingParties,
        isDisabled,
        setIsDisabled
    } = props;

    const roleTypeOptions = useMemo(() => {
        if (axnAdminUserPropertyBundles.some(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION)) {
            return USER_ROLE_TYPE_OPTIONS;
        }
        return [];
    }, [axnAdminUserPropertyBundles]);

    const [requestInProgress, setRequestInProgress] = useState(false);
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [axnAdminUserInvitationState, setAxnAdminUserInvitationState] = useState(axnAdminUserInvitation);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const updateOrganizationInvitationDetailsResponse = await updateOrganizationInvitationDetailsReduxRequest(dispatch)(
            axnAdminUserInvitationState,
            axnAdminUserPropertyBundles,
            axnCompanies,
            axnRelyingParties
        );

        if (updateOrganizationInvitationDetailsResponse) {
            setAxnAdminUserInvitationState(updateOrganizationInvitationDetailsResponse);
        }
        setRequestInProgress(false);
        setIsDisabled(true);
    };

    const handleRoleTypeChange = (newValue: SelectOption) => {
        const roleBundles = axnAdminUserPropertyBundles.filter(bundle => bundle.type === newValue.value);
        let newAxnAdminUserPropertyBundle = null;
        if (newValue.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION && roleBundles.length > 0) {
            newAxnAdminUserPropertyBundle = roleBundles[0];
        }

        setAxnAdminUserInvitationState({
            ...axnAdminUserInvitationState,
            organizationRole: newAxnAdminUserPropertyBundle,
            relyingPartyRoles: []
        });
    };

    const handleUpdateOrganizationRole = (newValue: SelectOption) => {
        const role = axnAdminUserPropertyBundles.find(bundle => bundle.shortName === newValue.value);

        if (role) {
            setAxnAdminUserInvitationState({
                ...axnAdminUserInvitationState,
                organizationRole: role
            });
        }
    };

    const addRelyingPartyRole = (newRelyingPartyRole: RelyingPartyRole) => {
        setAxnAdminUserInvitationState({
            ...axnAdminUserInvitationState,
            relyingPartyRoles: [...axnAdminUserInvitationState.relyingPartyRoles, newRelyingPartyRole]
        });
    };

    const removeRelyingPartyRole = (newRelyingPartyRole: RelyingPartyRole) => {
        setAxnAdminUserInvitationState({
            ...axnAdminUserInvitationState,
            relyingPartyRoles: axnAdminUserInvitationState.relyingPartyRoles.filter(
                rpr => rpr.relyingParty.id !== newRelyingPartyRole.relyingParty.id
            )
        });
    };

    const updateRelyingPartyRole = (newRelyingPartyRole: RelyingPartyRole) => {
        setAxnAdminUserInvitationState({
            ...axnAdminUserInvitationState,
            relyingPartyRoles: axnAdminUserInvitationState.relyingPartyRoles.map(rpr => {
                if (rpr.relyingParty.id === newRelyingPartyRole.relyingParty.id) {
                    return newRelyingPartyRole
                }
                return rpr;
            })
        })
    };

    const handleStatusChange = () => {
        const newStatus = axnAdminUserInvitationState.status === "active" ? "canceled" : "active";
        setAxnAdminUserInvitationState({
            ...axnAdminUserInvitationState,
            status: newStatus
        })
    };

    const onCloseClick = () => {
        navigateToUtil(ORGANIZATION_INVITATIONS_URL, navigate, routerLocation.pathname);
    };

    const onEditClick = () => {
        setIsDisabled(false);
    };

    const onCancelClick = () => {
        setAxnAdminUserInvitationState(axnAdminUserInvitation);
        setIsDisabled(true);
    };

    const selectedOrganizationOption: SelectOption = useMemo(() => {
        if (axnAdminUserInvitationState.organization) {
            return {
                label: axnAdminUserInvitationState.organization.name,
                value: axnAdminUserInvitationState.organization.id
            };
        }

        return null;
    }, [axnAdminUserInvitationState.organization]);

    const selectedRoleTypeOption: SelectOption = useMemo(() => {
        if (axnAdminUserInvitationState.organizationRole) {
            return USER_ROLE_TYPE_OPTIONS[0];
        } else if (!axnAdminUserInvitationState.organization) {
            return { label: "IDDW Admin", value: "IDDW Admin" };
        }

        return USER_ROLE_TYPE_OPTIONS[1];
    }, [axnAdminUserInvitationState.organizationRole]);

    const selectedOrganizationRoleOption: SelectOption = useMemo(() => {
        if (axnAdminUserInvitationState.organizationRole) {
            return {
                label: axnAdminUserInvitationState.organizationRole.name,
                value: axnAdminUserInvitationState.organizationRole.shortName
            };
        }

        return null;
    }, [axnAdminUserInvitationState.organizationRole]);

    const allowedAxnRelyingParties: Array<AxnRelyingParty> = useMemo(() => {
        return getAllowedRelyingPartiesForOrganizationId(
            axnRelyingParties,
            axnAdminUserInvitationState.organization?.id
        );
    }, [axnRelyingParties, axnAdminUserInvitationState.organization]);

    const axnAdminUserPropertyBundleOptions = useMemo(() => {
        return axnAdminUserPropertyBundles
            .filter(bundle => bundle.type === selectedRoleTypeOption.value)
            .map(bundle => ({ label: bundle.name, value: bundle.shortName }));
    }, [axnAdminUserPropertyBundles, selectedRoleTypeOption]);

    const canModifyInvitation = useMemo(() => {
        if (session.user.axnAdminUserRPProperties.length !== 0) {
            const allowedRelyingParties = extractRelyingPartiesForPropertyName(
                UserPermissionTypes.MODIFY_USERS,
                session.user.axnAdminUserRPProperties,
                axnRelyingParties
            ).map(rp => rp.id);

            return isAxnAdminUser(session.user) || allowedRelyingParties.length > 0;
        } else {
            const canModifyOrgUser = session.user.axnAdminUserOrganizationProperties.some(
                property => property.propertyName === UserPermissionTypes.MODIFY_USERS &&
                    isStringTrue(property.propertyValue)
            );

            return isAxnAdminUser(session.user) || canModifyOrgUser;
        }
    }, [
        session.user,
        axnRelyingParties
    ]);

    const relyingPartyRolesWithPermissions = useMemo(() => {
        return getRelyingPartyRolesWithPermissions(
            session,
            null,
            axnRelyingParties,
            axnAdminUserInvitationState.relyingPartyRoles
        );
    }, [
        axnAdminUserInvitationState.relyingPartyRoles,
        axnRelyingParties,
        session
    ]);

    const sessionUserHasAccessToAllAdminUserRPs = useMemo(() => {
        //get all of the relying party IDs the users being viewed has access to        
        const userRelyingPartyIds = axnAdminUserInvitationState.relyingPartyRoles.map(rpr => rpr.relyingParty.id);

        //get all of the relying party IDs that the logged in user can view
        const sessionUserViewRelyingPartyIds = relyingPartyRolesWithPermissions.filter(rpr => rpr.canView).map(rpr => rpr.relyingPartyRole.relyingParty.id);
        
        //check to see if we have access to every relying party ID the viewed user has
        return userRelyingPartyIds.every(id => sessionUserViewRelyingPartyIds.includes(id));
    }, [
        axnAdminUserInvitationState.relyingPartyRoles,
        relyingPartyRolesWithPermissions
    ]);


    const detailsResources = resources.pages.organizationInvitationDetails;

    return (
        <div className={styles.organizationInvitationDetailsComponent}>
            <Form onSubmit={handleSubmit}>
                <InputFormGroup
                    controlId="organization-invitation-email-input"
                    automationId="organization-invitation-email-input"
                    label={detailsResources.inputs.email.label}
                    ariaLabel={detailsResources.inputs.email.label}
                    value={axnAdminUserInvitationState.email || ""}
                    disabled={true}
                    isRequired={true}
                    onChange={() => { }}
                    labelWidth={labelWidth}
                />
                <OrganizationSelectFormGroup
                    idPrefix="invitation"
                    onChange={() => { }}
                    axnCompanies={axnCompanies}
                    selectedOption={selectedOrganizationOption}
                    disabled={true}
                    isRequired={!!axnAdminUserInvitationState.organization}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                />
                {roleTypeOptions.length > 0 && (
                    <SelectFormGroup
                        controlId={"user-role-type"}
                        automationId={"user-role-type"}
                        label={resources.modals.addOrganizationUser.inputs.roleType.label}
                        ariaLabel={resources.modals.addOrganizationUser.inputs.roleType.label}
                        disabled={isDisabled || !axnAdminUserInvitationState.organization}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={false}
                        labelWidth={labelWidth}
                        onChange={handleRoleTypeChange}
                        options={roleTypeOptions}
                        selectedOption={selectedRoleTypeOption}
                    />
                )}
                {selectedRoleTypeOption.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION && (
                    <>
                        <SelectFormGroup
                            controlId="organization-invitation-role-select"
                            automationId="organization-invitation-role-select"
                            ariaLabel={detailsResources.inputs.role.label}
                            label={detailsResources.inputs.role.label}
                            labelWidth={labelWidth}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            onChange={handleUpdateOrganizationRole}
                            options={axnAdminUserPropertyBundleOptions}
                            selectedOption={selectedOrganizationRoleOption}
                        />
                        <LabelValueContainer
                            controlId={`organization-user-role-description`}
                            label={" "}
                            labelIsBold={false}
                            labelWidth={labelWidth}
                            layout={LayoutTypes.ROW}
                            value={axnAdminUserInvitationState.organizationRole?.description}
                        />
                    </>
                )}
                {selectedRoleTypeOption.value === AxnAdminUserPropertyBundleTypes.RP && (
                    <>
                        <RelyingPartyRolesContainer
                            isDisabled={isDisabled}
                            requiredUserPermissionType={UserPermissionTypes.CREATE_USERS}
                            sessionUserHasAccessToAllAdminUserRPs={sessionUserHasAccessToAllAdminUserRPs}
                            relyingPartyRolesWithPermissions={relyingPartyRolesWithPermissions}
                            axnRelyingParties={allowedAxnRelyingParties}
                            axnAdminUserPropertyBundles={axnAdminUserPropertyBundles}
                            axnAdminUserPropertyBundleOptions={axnAdminUserPropertyBundleOptions}
                            labelWidth={labelWidth}
                            addRelyingPartyRole={addRelyingPartyRole}
                            removeRelyingPartyRole={removeRelyingPartyRole}
                            updateRelyingPartyRole={updateRelyingPartyRole}
                        />
                        {axnAdminUserInvitationState.relyingPartyRoles.length === 0 && (
                            <Alert variant={"danger"}>
                                {resources.modals.addOrganizationUser.noRelyingPartyRolesError}
                            </Alert>
                        )}
                    </>
                )}
                <ToggleFormGroup
                    controlId="organization-invitation-status-toggle"
                    automationId="organziation-invitation-status-toggle"
                    label={detailsResources.inputs.status.label}
                    ariaLabel={detailsResources.inputs.status.label}
                    labelWidth={labelWidth}
                    disabled={isDisabled}
                    isRequired={true}
                    onChange={handleStatusChange}
                    checked={axnAdminUserInvitationState.status === "active"}
                    afterInputLabel={axnAdminUserInvitationState.status}
                    value={axnAdminUserInvitationState.status}
                    checkedIcon={false}
                    uncheckedIcon={false}
                />
                <div className={styles.buttonDiv}>
                    <Button id="organization-invitation-details-close-button" onClick={onCloseClick} variant={"outline-secondary"}>{resources.common.buttons.close.label}</Button>
                    {isDisabled && canModifyInvitation && (
                        <Button
                            id="organization-invitation-details-edit-button"
                            onClick={onEditClick}
                        >
                            {resources.common.buttons.edit.label}
                        </Button>
                    )}
                    {!isDisabled && (
                        <>
                            <Button id="organization-invitation-details-cancel-button" onClick={onCancelClick} variant={"outline-secondary"}>{resources.common.buttons.cancel.label}</Button>
                            <ButtonWithSpinner
                                id="organization-invitation-details-submit-button"
                                disabled={requestInProgress}
                                hideSpinner={false}
                                variant="primary"
                                type={ButtonTypes.SUBMIT}
                                label={resources.common.buttons.save.label}
                            />
                        </>
                    )}
                </div>
            </Form>
        </div>
    );
};